import { Component, Inject, Injector, Input } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { InstanceResponseDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { BaseInstanceResponseDto } from '@cloud-twin/master-operator/models/master-operator-instance';
import { MasterOperatorType } from '@cloud-twin/master-operator/models/master-operator-type';
import { MasterOperatorService } from '@cloud-twin/master-operator/services/master-operator.service';
import {
  TabDetailPanelParameters,
  TabDetailParameterName,
} from '@common-modules/dependencies/navigation/tab-detail-panel-parameters';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout.tokens';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { AuthorizeService } from '@common-modules/shared/auth/services/authorize.service';
import { BaseWidgetComponent } from '@common-modules/shared/component/base-widget.component';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { WlmDialogSettings } from '@common-modules/shared/model/dialog/wlm-dialog-setting';
import { GroupDto } from '@common-modules/shared/model/roles/group.dto';
import { UserDto } from '@common-modules/shared/model/roles/user.dto';
import { SelectOption } from '@common-modules/shared/model/shared/select-option';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';
import { finalize } from 'rxjs';
import { GroupModelsDto } from '../models/group-models.dto';
import { GroupModelsService } from '../services/group-models.service';
import { UsersService } from '../services/users.service';

export interface additionalSelectOption extends SelectOption<string> {
  canAccess: boolean;
}

const COMPONENT_SELECTOR = 'tc-models-security-widget';
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './models-security-widget.component.html',
  styleUrl: './models-security-widget.component.scss',
  standalone: false,
  host: {
    class: 'water-grid',
  },
})
export class ModelsSecurityWidgetComponent extends BaseWidgetComponent {
  T_SCOPE = `${AppModules.WlmSecurity}.${COMPONENT_SELECTOR}`;
  T_SCOPE_DIALOGS = `${AppModules.Dialogs}.crud-names`;

  get componentName(): string {
    return 'ModelsSecurityWidgetComponent';
  }

  componentCrud = 'WLMAdministrationCrud';

  @Input() fullSize = true;
  public isLoading = false;

  private _instances: InstanceResponseDto[];
  instanceOptions: additionalSelectOption[];
  canAccess: boolean;
  group: GroupDto;
  user: UserDto;
  selectedGroup: GroupDto;
  editingMode: boolean = true;
  isReadOnly: boolean = false;
  canProcess: boolean = false;
  private setLoading: (loading: boolean) => void;

  mapInitParameters(parameters: TabDetailPanelParameters) {
    this.group = parameters.parameters.get(TabDetailParameterName.Group);
    this.user = parameters.parameters.get(TabDetailParameterName.user);
    this.isReadOnly =
      parameters.parameters.has(TabDetailParameterName.user) || this.group?.groupId === undefined;

    if (this.user?.userCode) {
      this.loadPermissionToEdit();
    }
  }

  init(): void {
    if (this.group?.groupId) {
      this.buildInstanceOptions(this.group.groupId);
    } else {
      this.buildInstanceOptions(undefined, this.user?.userCode);
    }
  }

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private _masterOperatorService: MasterOperatorService,
    private _dialogService: DialogService,
    private _groupModelsService: GroupModelsService,
    private _usersService: UsersService,
    private _spinnerService: SpinnerService,
    private _authorizeService: AuthorizeService
  ) {
    super(injector, widgetSettings);
    this.setLoading = this._spinnerService.buildSetLoadingFn();
  }

  buildInstanceOptions(groupId?: number, usercode?: string): void {
    this.setLoading(true);

    this._masterOperatorService
      .getInstances({ type: MasterOperatorType.Master })
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe((activeInstances) => {
        this._instances = activeInstances;

        this.instanceOptions = this._instances.map((instance) => ({
          label: this._masterOperatorService.instanceReadableLabel(
            instance as BaseInstanceResponseDto
          ),
          value: instance.instanceId,
          url: instance.url,
          canAccess: false,
        }));

        if (groupId || usercode) {
          this.canProcess = true;
        }

        if (groupId) {
          this.processGroupId(groupId);
        } else if (usercode) {
          this._usersService.getUserByCode(usercode).subscribe((user: UserDto) => {
            if (user.groupIds && user.groupIds.length > 0) {
              user.groupIds.forEach((groupId) => {
                this.processGroupId(groupId);
              });
            }
          });
        }
      });
  }

  private processGroupId(groupId: number): void {
    this._groupModelsService.getGroupModelsCrudByGroupId(groupId).subscribe((response) => {
      response.groupModels.forEach((groupModel) => {
        const instanceOption = this.instanceOptions.find(
          (option) => option.value === groupModel.instanceId
        );

        if (instanceOption) {
          if (!instanceOption.canAccess && groupModel.write && groupModel.read) {
            instanceOption.canAccess = true;
          }
        }
      });
    });
  }

  savegroupModelCrud(checkBoxComponent: MatCheckbox, checkedState: boolean, value: any) {
    const groupModelCrud = new GroupModelsDto();
    this.isLoading = true;
    checkBoxComponent.indeterminate = true;

    groupModelCrud.read = checkedState;
    groupModelCrud.write = checkedState;
    groupModelCrud.groupId = this.group?.groupId;
    groupModelCrud.instanceId = value.value;

    this._groupModelsService.postGroupModelsCrud(groupModelCrud).subscribe({
      next: (response) => {
        this.saveFinished(checkBoxComponent);

        if (response.errors.length) {
          this._dialogService.showTranslatedMessage(
            new WlmDialogSettings({
              translateKey: `${this.T_SCOPE}.messages.update-permission-error`,
              icon: 'error',
            })
          );
        } else {
          value.canAccess = checkedState;

          this._dialogService.showTranslatedMessageInSnackBar(
            new WlmDialogSettings({
              translateKey: `${this.T_SCOPE}.messages.update-permission-success`,
            })
          );
        }
      },
      error: () => {
        this.saveFinished(checkBoxComponent);
        checkBoxComponent.checked = !checkedState;
      },
    });
  }

  getSelectedGroup(item: GroupDto) {
    this.selectedGroup = item;
  }

  saveFinished(checkBoxComponent: MatCheckbox) {
    checkBoxComponent.indeterminate = false;
    this.isLoading = false;
  }

  private loadPermissionToEdit() {
    this._authorizeService.canAccess(this.componentCrud, 'u').subscribe({
      next: (canAccess) => {
        this.editingMode = canAccess;
      },
    });
  }
}
